import { Modal, TableRow } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
    AvailableOutsourceImportMaterial,
    Material,
    MaterialOutsourceStandard,
    OutsourceOrder,
    OutsourceOrderImportRequest,
    OutsourceOrderImportRequestLine,
} from "../../../../data/entities";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import DropdownMenuWithSearch from "../../../../shared/components/DropdownMenuWithSearch";
import IconButton from "../../../../shared/components/IconButton";
import TextBox from "../../../../shared/components/TextBox";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import apiMethods from "../../../../utils/api";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { error, success } from "../../../../utils/toast";
import { StyledBackdrop } from "../../../MaterialOrder/single/AddMaterialModal/Styled";
import { ProductButtonRow } from "../../../ProductOrder/single/Styled";
import {
    CountBadge,
    EmptyState,
    LedgerSection,
    LedgerSectionHeader,
    LedgerSubtitle,
    LedgerTitle,
    LedgerTitleGroup,
    OperationField,
    OperationFieldGrid,
    OperationFooter,
    OperationMetric,
    OperationMetricLabel,
    OperationMetricValue,
    OperationModalBody,
    OperationModalFrame,
    OperationModalHeader,
    OperationPanel,
    OperationPanelTitle,
    OperationSubtitle,
    OperationSummaryStrip,
    OperationTitle,
    OperationTitleGroup,
    OperationWarning,
    TruncatedCellText,
} from "../../components";
import { StyledButton } from "../../Styled";

const RequestColumns = [
    { text: "Ngày đề nghị", icon: <></>, width: "130px" },
    { text: "Trạng thái", icon: <></>, width: "160px" },
    { text: "Vật tư nhận về", icon: <></> },
    { text: "Đã nhập / Đề nghị", icon: <></>, width: "160px" },
    { text: "", icon: <></>, width: "120px" },
];

const DraftLineColumns = [
    { text: "Vật tư nhận về", icon: <></> },
    { text: "Số lượng đề nghị", icon: <></>, width: "160px" },
    { text: "Định mức nguồn", icon: <></> },
    { text: "", icon: <></>, width: "56px" },
];

const RequestStatusLabel: Record<string, string> = {
    OPEN: "Đang mở",
    PARTIALLY_IMPORTED: "Nhập một phần",
    IMPORTED: "Đã nhập",
    CANCELLED: "Đã huỷ",
};

type DraftImportLine = {
    localId: string;
    output_material: Material;
    requested_quantity: number;
    note?: string;
    standards: MaterialOutsourceStandard[];
    missing_source_materials?: Material[];
    is_available?: boolean;
};

const getLineRemaining = (line: OutsourceOrderImportRequestLine) =>
    Number(line.remaining_quantity ?? (line.requested_quantity || 0) - (line.imported_quantity || 0));

const getRequestRemaining = (request: OutsourceOrderImportRequest) =>
    (request.lines || []).reduce((total, line) => total + Math.max(getLineRemaining(line), 0), 0);

const formatMaterialName = (material?: Material | null) =>
    material ? `${material.material_code || material.id} - ${material.name}` : "Chọn vật tư nhận về";

const formatRequestDate = (value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("vi-VN");
};

const formatStandardPreview = (standards: MaterialOutsourceStandard[], outputQuantity: number) =>
    standards.map((standard) => {
        const sourceQuantity = Number(standard.source_quantity_per_output || 0) * Number(outputQuantity || 0);
        return `${standard.source_material?.name || "-"}: ${formatInternationalNumber(sourceQuantity)}`;
    }).join("; ");

const RequestRow = ({ item, extraRowProps }: any) => {
    const lines = item.lines || [];
    const remaining = getRequestRemaining(item);
    const imported = lines.reduce((total: number, line: any) => total + Number(line.imported_quantity || 0), 0);
    const requested = lines.reduce((total: number, line: any) => total + Number(line.requested_quantity || 0), 0);
    const materialNames = lines.map((line: any) => line.output_material?.name).filter(Boolean).join(", ");

    return (
        <TableRow>
            <StyledTableCell align="left">{formatRequestDate(item.request_date)}</StyledTableCell>
            <StyledTableCell align="left">{RequestStatusLabel[item.status] || item.status}</StyledTableCell>
            <StyledTableCell align="left">
                <TruncatedCellText title={materialNames}>{materialNames || "-"}</TruncatedCellText>
            </StyledTableCell>
            <StyledTableCell align="left">
                {formatInternationalNumber(imported)} / {formatInternationalNumber(requested)}
            </StyledTableCell>
            <StyledTableCell align="left">
                {remaining > 0 && (
                    <StyledButton variant="outlined" onClick={() => extraRowProps.onImport(item)}>
                        Nhập kho
                    </StyledButton>
                )}
            </StyledTableCell>
        </TableRow>
    );
};

const DraftLineRow = ({ item, extraRowProps }: any) => (
    <TableRow>
        <StyledTableCell align="left">
            <TruncatedCellText title={formatMaterialName(item.output_material)}>
                {formatMaterialName(item.output_material)}
            </TruncatedCellText>
        </StyledTableCell>
        <StyledTableCell align="left">
            <TextBox
                numeric
                text={formatInternationalNumber(item.requested_quantity ?? "")}
                onChangeText={(value: string) => {
                    const n = parseInternationalToNumber(value, error);
                    if (Number.isFinite(n)) extraRowProps.onChangeQuantity(item.localId, n);
                }}
            />
        </StyledTableCell>
        <StyledTableCell align="left">
            {formatStandardPreview(item.standards, item.requested_quantity)}
        </StyledTableCell>
        <StyledTableCell align="left">
            <IconButton onClick={() => extraRowProps.onRemove(item.localId)}>
                <DeleteIcon />
            </IconButton>
        </StyledTableCell>
    </TableRow>
);

const CreateImportRequestModal = ({
    open,
    handleClose,
    outsourceOrder,
    fetchOutsourceOrder,
}: {
    open: boolean;
    handleClose: () => void;
    outsourceOrder: OutsourceOrder;
    fetchOutsourceOrder?: () => Promise<any>;
}) => {
    const [availableMaterials, setAvailableMaterials] = useState<AvailableOutsourceImportMaterial[]>([]);
    const [isLoadingAvailableMaterials, setIsLoadingAvailableMaterials] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [rows, setRows] = useState<DraftImportLine[]>([]);
    const [note, setNote] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !outsourceOrder?.id) return;

        let isMounted = true;
        setRows([]);
        setNote("");
        setLoadError(false);
        setIsLoadingAvailableMaterials(true);
        apiMethods
            .get(`/materials/outsource-orders/${outsourceOrder.id}/available-import-materials/`)
            .then((response: any) => {
                if (isMounted) setAvailableMaterials(response || []);
            })
            .catch((err) => {
                if (isMounted) {
                    setAvailableMaterials([]);
                    setLoadError(true);
                }
                error("Không tải được danh sách vật tư nhận về", err);
            })
            .finally(() => {
                if (isMounted) setIsLoadingAvailableMaterials(false);
            });

        return () => {
            isMounted = false;
        };
    }, [open, outsourceOrder?.id]);

    const availableOutputs = useMemo(() => {
        const selectedIds = new Set(rows.map((row) => String(row.output_material.id)));
        return availableMaterials
            .filter((item) => !selectedIds.has(String(item.material.id)))
            .map((item) => ({
                ...item.material,
                outsource_standards: item.standards,
                missing_source_materials: item.missing_source_materials || [],
                is_available: item.is_available !== false,
            }));
    }, [availableMaterials, rows]);

    const onSelectOutputMaterial = (
        material: Material & {
            outsource_standards?: MaterialOutsourceStandard[];
            missing_source_materials?: Material[];
            is_available?: boolean;
        },
    ) => {
        const standards = material.outsource_standards || [];
        if (!standards.length) {
            error("Không thể tạo đề nghị", "Vật tư nhận về chưa có định mức gia công phù hợp với đơn này");
            return;
        }

        setRows((currentRows) => [
            ...currentRows,
            {
                localId: `line-${Date.now()}-${material.id}`,
                output_material: material,
                requested_quantity: 1,
                standards,
                missing_source_materials: material.missing_source_materials || [],
                is_available: material.is_available !== false,
            },
        ]);
    };

    const onChangeQuantity = (localId: string, quantity: number) => {
        setRows((currentRows) =>
            currentRows.map((row) => row.localId === localId ? { ...row, requested_quantity: quantity } : row),
        );
    };

    const onSubmit = async () => {
        if (!rows.length || rows.some((row) => !Number.isFinite(Number(row.requested_quantity)) || Number(row.requested_quantity) <= 0 || row.is_available === false)) {
            error("Không thể tạo đề nghị", "Vui lòng chọn vật tư nhận về và nhập số lượng lớn hơn 0");
            return;
        }
        setIsSubmitting(true);
        try {
            await apiMethods.post(`/materials/outsource-orders/${outsourceOrder.id}/import-requests/`, {
                note,
                lines: rows.map((row) => ({
                    output_material_id: row.output_material.id,
                    requested_quantity: row.requested_quantity,
                    note: row.note || "",
                })),
            });
            await fetchOutsourceOrder?.();
            success("THÀNH CÔNG", "Đã tạo đề nghị nhập kho gia công");
            handleClose();
        } catch (err) {
            error("Tạo đề nghị nhập kho thất bại", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{ timeout: 500, onClick: (e) => e.stopPropagation() }}
        >
            <OperationModalFrame $isOpen={open}>
                <OperationModalHeader>
                    <OperationTitleGroup>
                        <OperationTitle>Tạo đề nghị nhập kho gia công</OperationTitle>
                        <OperationSubtitle>
                            Chọn vật tư nhận về; hệ thống kiểm tra định mức và snapshot tỉ lệ tại thời điểm tạo.
                        </OperationSubtitle>
                    </OperationTitleGroup>
                </OperationModalHeader>

                <OperationModalBody>
                    <OperationPanel>
                        <OperationPanelTitle>Vật tư nhận về</OperationPanelTitle>
                        <OperationFieldGrid>
                            <OperationField>
                                Chọn vật tư
                                <DropdownMenuWithSearch
                                    menuList={availableOutputs}
                                    menuText="Chọn vật tư nhận về"
                                    handleOnclickItem={onSelectOutputMaterial}
                                    isView={isSubmitting}
                                />
                            </OperationField>
                            <OperationField>
                                Ghi chú
                                <TextBox
                                    placeholder="Ghi chú đề nghị"
                                    text={note}
                                    onChangeText={(value: string) => setNote(value)}
                                />
                            </OperationField>
                        </OperationFieldGrid>
                        {isLoadingAvailableMaterials && <OperationWarning>Đang tải danh sách vật tư nhận về.</OperationWarning>}
                        {loadError && <OperationWarning>Không tải được danh sách vật tư nhận về.</OperationWarning>}
                        {!isLoadingAvailableMaterials && !loadError && availableMaterials.length === 0 && (
                            <OperationWarning>
                                Không có vật tư nhận về nào có định mức gia công phù hợp với vật tư mang đi trong đơn này.
                            </OperationWarning>
                        )}
                    </OperationPanel>

                    <OperationPanel>
                        <OperationPanelTitle>Preview định mức snapshot</OperationPanelTitle>
                        {rows.filter(row => row.missing_source_materials?.length).map(row => <OperationWarning key={row.localId}>Thiếu nguồn: {row.missing_source_materials?.map(m => m.name).join(", ")}</OperationWarning>)}
                        <CustomTable
                            itemList={rows}
                            RowElement={DraftLineRow}
                            TableColumns={DraftLineColumns}
                            extraRowProps={{
                                onChangeQuantity,
                                onRemove: (localId: string) =>
                                    setRows((currentRows) => currentRows.filter((row) => row.localId !== localId)),
                            }}
                        />
                        {rows.length === 0 && (
                            <EmptyState>
                                <div className="title">Chưa chọn vật tư nhận về</div>
                                <div className="body">Chọn vật tư để xem định mức nguồn sẽ bị tiêu hao.</div>
                            </EmptyState>
                        )}
                    </OperationPanel>
                </OperationModalBody>

                <OperationFooter>
                    <StyledButton variant="outlined" onClick={handleClose}>Huỷ bỏ</StyledButton>
                    <StyledButton
                        variant="contained"
                        onClick={onSubmit}
                        disabled={
                            isSubmitting ||
                            rows.length === 0 ||
                            rows.some((row) => !Number.isFinite(Number(row.requested_quantity)) || row.requested_quantity <= 0 || row.is_available === false)
                        }
                    >
                        Tạo đề nghị
                    </StyledButton>
                </OperationFooter>
            </OperationModalFrame>
        </Modal>
    );
};

const ImportReceiptModal = ({
    open,
    handleClose,
    outsourceOrder,
    request,
    balanceSummary,
    fetchOutsourceOrder,
}: {
    open: boolean;
    handleClose: () => void;
    outsourceOrder: OutsourceOrder;
    request: OutsourceOrderImportRequest | null;
    balanceSummary?: any;
    fetchOutsourceOrder?: () => Promise<any>;
}) => {
    const [quantities, setQuantities] = useState<Record<string, number>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open) setQuantities({});
    }, [open, request?.id]);

    const sourceBalanceById = useMemo(() => {
        const map = new Map<string, any>();
        (balanceSummary?.source_materials || []).forEach((item: any) => {
            if (item.material?.id) map.set(String(item.material.id), item);
        });
        return map;
    }, [balanceSummary]);

    const sourceUsage = useMemo(() => {
        const usage = new Map<string, { material: Material; quantity: number }>();
        (request?.lines || []).forEach((line) => {
            const quantity = Number(quantities[String(line.id)] || 0);
            if (quantity <= 0) return;
            (line.standard_snapshots || []).forEach((snapshot) => {
                const sourceId = String(snapshot.source_material.id);
                const current = usage.get(sourceId) || { material: snapshot.source_material, quantity: 0 };
                current.quantity += quantity * Number(snapshot.source_quantity_per_output || 0);
                usage.set(sourceId, current);
            });
        });
        return Array.from(usage.entries()).map(([sourceId, item]) => ({
            sourceId,
            ...item,
            holding: Number(sourceBalanceById.get(sourceId)?.provider_holding_quantity || 0),
        }));
    }, [quantities, request, sourceBalanceById]);

    const hasQuantity = Object.values(quantities).some((quantity) => Number(quantity) > 0);
    const exceedsLineRemaining = (request?.lines || []).some(
        (line) => Number(quantities[String(line.id)] || 0) > getLineRemaining(line),
    );

    const onSubmit = async () => {
        if (!request || !hasQuantity) {
            error("Không thể nhập kho", "Vui lòng nhập số lượng thực nhận");
            return;
        }
        if (exceedsLineRemaining) {
            error("Không thể nhập kho", "Số lượng nhập vượt số lượng đề nghị còn lại");
            return;
        }

        setIsSubmitting(true);
        try {
            await apiMethods.post(`/materials/outsource-orders/${outsourceOrder.id}/import-requests/${request.id}/imports/`, {
                lines: Object.entries(quantities)
                    .filter(([, quantity]) => Number(quantity) > 0)
                    .map(([requestLineId, quantity]) => ({
                        request_line_id: requestLineId,
                        quantity,
                    })),
            });
            await fetchOutsourceOrder?.();
            success("THÀNH CÔNG", "Đã nhập kho vật tư gia công");
            handleClose();
        } catch (err) {
            error("Nhập kho gia công thất bại", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{ timeout: 500, onClick: (e) => e.stopPropagation() }}
        >
            <OperationModalFrame $isOpen={open}>
                <OperationModalHeader>
                    <OperationTitleGroup>
                        <OperationTitle>Nhập kho theo đề nghị</OperationTitle>
                        <OperationSubtitle>
                            Nhập số lượng thực nhận; hệ thống trừ vật tư nguồn theo snapshot định mức của đề nghị.
                        </OperationSubtitle>
                    </OperationTitleGroup>
                </OperationModalHeader>

                <OperationModalBody>
                    <OperationPanel>
                        <OperationPanelTitle>Vật tư thực nhận</OperationPanelTitle>
                        {(request?.lines || []).map((line) => {
                            const remaining = getLineRemaining(line);
                            return (
                                <OperationFieldGrid key={line.id}>
                                    <OperationField>
                                        {line.output_material?.name || "Vật tư nhận về"}
                                        <TextBox
                                            numeric
                                            placeholder="Số lượng nhập"
                                            text={formatInternationalNumber(quantities[String(line.id)] || "")}
                                            onChangeText={(value: string) => {
                                                const n = parseInternationalToNumber(value, error);
                                                if (Number.isFinite(n)) {
                                                    setQuantities((current) => ({
                                                        ...current,
                                                        [String(line.id)]: n,
                                                    }));
                                                }
                                            }}
                                        />
                                    </OperationField>
                                    <OperationField>
                                        Còn lại theo đề nghị
                                        <OperationMetricValue>{formatInternationalNumber(remaining)}</OperationMetricValue>
                                    </OperationField>
                                </OperationFieldGrid>
                            );
                        })}
                        {exceedsLineRemaining && (
                            <OperationWarning>Số lượng nhập vượt số lượng đề nghị còn lại.</OperationWarning>
                        )}
                    </OperationPanel>

                    <OperationPanel>
                        <OperationPanelTitle>Tiêu hao vật tư nguồn</OperationPanelTitle>
                        <OperationSummaryStrip>
                            {sourceUsage.map((item) => (
                                <OperationMetric key={item.sourceId}>
                                    <OperationMetricLabel>{item.material.name}</OperationMetricLabel>
                                    <OperationMetricValue>
                                        {formatInternationalNumber(item.quantity)} / còn {formatInternationalNumber(item.holding - item.quantity)}
                                    </OperationMetricValue>
                                </OperationMetric>
                            ))}
                        </OperationSummaryStrip>
                        {sourceUsage.length === 0 && (
                            <EmptyState>
                                <div className="title">Chưa có số lượng nhập</div>
                                <div className="body">Nhập số lượng thực nhận để xem lượng nguồn sẽ bị tiêu hao.</div>
                            </EmptyState>
                        )}
                    </OperationPanel>
                </OperationModalBody>

                <OperationFooter>
                    <StyledButton variant="outlined" onClick={handleClose}>Huỷ bỏ</StyledButton>
                    <StyledButton
                        variant="contained"
                        onClick={onSubmit}
                        disabled={isSubmitting || !hasQuantity || exceedsLineRemaining}
                    >
                        Nhập kho
                    </StyledButton>
                </OperationFooter>
            </OperationModalFrame>
        </Modal>
    );
};

interface ImportRequestListProps {
    outsourceOrder: OutsourceOrder;
    balanceSummary?: any;
    fetchOutsourceOrder?: () => Promise<any>;
    isView?: boolean;
}

export const ImportRequestList = ({
    outsourceOrder,
    balanceSummary,
    fetchOutsourceOrder,
}: ImportRequestListProps) => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<OutsourceOrderImportRequest | null>(null);
    const importRequests = useMemo(
        () => ((outsourceOrder as any).import_requests || balanceSummary?.import_requests || []).filter(
            (request: OutsourceOrderImportRequest) => request.status !== "CANCELLED",
        ),
        [balanceSummary?.import_requests, outsourceOrder],
    );
    const canCreateRequest = Boolean(outsourceOrder?.id) && !["Completed", "Cancelled"].includes(String(outsourceOrder?.status));

    return (
        <LedgerSection>
            {isCreateModalOpen && (
                <CreateImportRequestModal
                    open={isCreateModalOpen}
                    handleClose={() => setIsCreateModalOpen(false)}
                    outsourceOrder={outsourceOrder}
                    fetchOutsourceOrder={fetchOutsourceOrder}
                />
            )}
            <ImportReceiptModal
                open={Boolean(selectedRequest)}
                handleClose={() => setSelectedRequest(null)}
                outsourceOrder={outsourceOrder}
                request={selectedRequest}
                balanceSummary={balanceSummary}
                fetchOutsourceOrder={fetchOutsourceOrder}
            />

            <LedgerSectionHeader>
                <LedgerTitleGroup>
                    <LedgerTitle>Đề nghị nhập kho</LedgerTitle>
                    <LedgerSubtitle>
                        Tạo đề nghị nhập kho cho vật tư nhận về.
                    </LedgerSubtitle>
                </LedgerTitleGroup>
                <CountBadge>{importRequests.length}</CountBadge>
            </LedgerSectionHeader>

            <CustomTable
                itemList={importRequests}
                RowElement={RequestRow}
                TableColumns={RequestColumns}
                extraRowProps={{ onImport: setSelectedRequest }}
            />
            {importRequests.length === 0 && (
                <EmptyState>
                    <div className="title">Chưa có đề nghị nhập kho</div>
                    <div className="body">Tạo đề nghị khi cần nhập vật tư gia công nhận về.</div>
                </EmptyState>
            )}

            {canCreateRequest && (
                <ProductButtonRow>
                    <StyledButton variant="contained" onClick={() => setIsCreateModalOpen(true)}>
                        Tạo đề nghị nhập kho
                    </StyledButton>
                </ProductButtonRow>
            )}
        </LedgerSection>
    );
};
